import React from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";

import { clx } from "../../../../utils/Helpers";
import "./UISelectCard.scss";

/**
 * Selectable card (title + badge, meta rows, chips) with an active side marker.
 * Used for roles navigation and crew size selection.
 * `selectionRole="radio"` exposes it as a radio inside a parent `role="radiogroup"`.
 */
const UISelectCard = ({
  title,
  badge,
  meta,
  chips,
  footer,
  size,
  selected,
  invalid,
  disabled,
  selectionRole,
  className,
  onClick,
}) => (
  <button
    type="button"
    className={clx(
      "ui-select-card",
      `ui-select-card--${size}`,
      {
        "ui-select-card--selected": selected,
        "ui-select-card--invalid": invalid,
      },
      className,
    )}
    role={selectionRole === "radio" ? "radio" : undefined}
    aria-checked={selectionRole === "radio" ? selected : undefined}
    aria-pressed={selectionRole === "button" ? selected : undefined}
    disabled={disabled}
    onClick={onClick}
  >
    <span className="ui-select-card__top">
      <Typography
        className={clx(
          "ui-select-card__title",
          size === "md" ? "text-heading-sm" : "text-label-md",
        )}
        component="span"
      >
        {title}
      </Typography>
      {badge != null && (
        <Typography className="ui-select-card__badge text-label-sm" component="span">
          {badge}
        </Typography>
      )}
    </span>
    {meta.map((row, index) => (
      <Typography
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        className={clx(
          "ui-select-card__meta",
          size === "md" ? "text-body-sm" : "text-label-md",
        )}
        component="span"
      >
        {row}
      </Typography>
    ))}
    {chips.length > 0 && (
      <Typography className="ui-select-card__chips text-label-md" component="span">
        {chips.map((chip, index) => (
          <span
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            className={clx("ui-select-card__chip", {
              [`ui-select-card__chip--${chip?.color}`]: chip?.color,
            })}
          >
            {chip?.label ?? chip}
          </span>
        ))}
      </Typography>
    )}
    {footer != null && <span className="ui-select-card__footer">{footer}</span>}
  </button>
);

UISelectCard.propTypes = {
  title: PropTypes.node.isRequired,
  badge: PropTypes.node,
  /** Each row is laid out with space-between (e.g. a label/value pair or one line). */
  meta: PropTypes.arrayOf(PropTypes.node),
  /** A chip is a node, or `{ label, color }` for a colored one. */
  chips: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.shape({
        label: PropTypes.node.isRequired,
        color: PropTypes.oneOf(["success"]),
      }),
    ]),
  ),
  /** Free slot under the content (e.g. a UIChip badge). */
  footer: PropTypes.node,
  /** sm: compact (roles navigation); md: larger title/body text (crew size). */
  size: PropTypes.oneOf(["sm", "md"]),
  selected: PropTypes.bool,
  invalid: PropTypes.bool,
  disabled: PropTypes.bool,
  selectionRole: PropTypes.oneOf(["button", "radio"]),
  className: PropTypes.string,
  onClick: PropTypes.func,
};

UISelectCard.defaultProps = {
  badge: null,
  meta: [],
  chips: [],
  footer: null,
  size: "sm",
  selected: false,
  invalid: false,
  disabled: false,
  selectionRole: "button",
  className: undefined,
  onClick: undefined,
};

export default UISelectCard;
